import { type ActionDispatch, useCallback, useEffect, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TAB_TYPES } from '../../constants/tab';
import translations from '../../translations';
import type { Tab, TabType } from '../../types/tab';
import type { ElementSelection } from '../page-editor/components/gui/composer/ComposerProvider';
import TabItem from './TabItem';
import type { TabAction } from './TabsProvider';

interface Props {
    tabs: Tab[];
    activeTabKey: string | null;
    onSelect: (tabType: TabType, tabKey: string) => void;
    onClose: (tabKey: string) => void;
    onReOrderTabs: (tabKey: string, index: number) => void;
    dispatchTabs: ActionDispatch<[action: TabAction]>;
    accountMode?: boolean;
}

const Tabs = ({
    tabs,
    activeTabKey,
    onSelect,
    onClose,
    onReOrderTabs,
    dispatchTabs,
    accountMode = false,
}: Props) => {
    const wrapperRef = useRef<HTMLDivElement>(null);

    const [showCloseButtons, setShowCloseButtons] = useState(false);
    const [showTitles, setShowTitles] = useState(false);

    const recalculateHidingCloseButtons = useCallback(() => {
        const wrapperWidth = wrapperRef.current?.clientWidth;

        if (wrapperWidth) {
            const elements = document.getElementsByClassName('tab-header-item');

            // if wrapper width can't fit the elements x100px
            // then hide the close button for tabs other than the active
            setShowCloseButtons(elements.length * 100 < wrapperWidth);

            // if wrapper width can't fit the elements x75px
            // then hide the title and icons for all tabs
            setShowTitles(elements.length * 75 < wrapperWidth);
        }
    }, []);

    useEffect(() => {
        const onResize = () => recalculateHidingCloseButtons();
        window.addEventListener('resize', onResize);

        return () => window.removeEventListener('resize', onResize);
    }, [recalculateHidingCloseButtons]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: We need to recalculate the hiding of close buttons whenever tabs change.
    useEffect(() => {
        recalculateHidingCloseButtons();
    }, [tabs, recalculateHidingCloseButtons]);

    const closeAllTabs = () => {
        dispatchTabs({ type: 'CLOSE_ALL_TABS' });

        onSelect('flows', 'flows');
    };

    const closeTabsToRight = (element: ElementSelection) => {
        dispatchTabs({ type: 'CLOSE_TABS_TO_RIGHT', payload: { tabKey: element.targetId! } });

        onSelect(element.tabType!, element.targetId!);
    };

    const closeOtherTabs = (element: ElementSelection) => {
        dispatchTabs({ type: 'CLOSE_OTHER_TABS', payload: { tabKey: element.targetId! } });

        onSelect(element.tabType!, element.targetId!);
    };

    return (
        <div ref={wrapperRef} className="tab-header-items-wrapper">
            <div className="tab-header-items">
                <DndProvider backend={HTML5Backend}>
                    {tabs?.map((tab) => (
                        <TabItem
                            key={tab.key}
                            isActive={tab.key === activeTabKey}
                            tabType={tab.type}
                            tabKey={tab.key}
                            elementId={tab.elementId}
                            onSelect={onSelect}
                            onClose={onClose}
                            showCloseButton={showCloseButtons}
                            title={
                                accountMode && tab.type === TAB_TYPES.tenant
                                    ? translations.TENANT_page_title_account
                                    : tab.title
                            }
                            showTitle={showTitles}
                            order={tab.order}
                            onReOrderTabs={onReOrderTabs}
                            closeAllTabs={closeAllTabs}
                            closeTabsToRight={closeTabsToRight}
                            closeOtherTabs={closeOtherTabs}
                        />
                    ))}
                </DndProvider>
            </div>
        </div>
    );
};

export default Tabs;
